import { useQuery, keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { useAxios } from "@/app/providers/AxiosProvider";
import handleError from "@lib/helpers/handleError";
import buildListUrl from "@lib/shared/queryString";

export interface Tag {
  id: number;
  name: string;
  slug: string;
}

export interface UseTagsOptions {
  page?: number;
  sortBy?: { property: string; direction: "asc" | "desc" };
  search?: string;
  filters?: Record<string, string | number | boolean | string[] | undefined>;
}

const DEFAULT_SORT = {
  property: "updated_at",
  direction: "desc",
} as const;

export default function useTags({
  page = 1,
  sortBy = DEFAULT_SORT,
  search = "",
  filters = {},
}: UseTagsOptions) {
  const api = useAxios();
  const client = useQueryClient();

  const url = buildListUrl(
    "/tags",
    { page, sortBy: sortBy.property, sortDirection: sortBy.direction, search },
    filters
  );

  // note isFetching is available to show a loading indicator while loading new data
  const { isPending, error, data } = useQuery({
    // The URL is the only input the queryFn reads, so keying on it covers every
    // page/sort/search/filter combination — otherwise staleTime would serve a
    // cached page for different arguments and pagination would appear broken.
    queryKey: ["tags", url],
    queryFn: () => api.get(url),
    placeholderData: keepPreviousData,
    // Tags change infrequently and are fetched on many pages; cache briefly to
    // avoid refetching the (relatively expensive) /tags endpoint each navigation.
    staleTime: 2 * 60_000,
  });

  handleError(error);

  const invalidate = () => {
    client.invalidateQueries({ queryKey: ["tags"] });
  };

  const tags: Tag[] = data?.data ?? [];

  return { tags, isLoading: isPending, invalidate };
}
